import React, { useState } from "react";
import { Pencil, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { coffreInfo, dateKey, COFFRE_EVERY, COFFRE_DEFAULT_MESSAGE } from "./scShared";
import { sfx } from "./nq/nqSound";

export const CoffreMessageField = ({ state, update, testid = "sc-coffre-message" }) => (
  <div>
    <label className="block text-sm font-semibold text-[var(--ink)] mb-1.5" htmlFor={testid}>Message d'encouragement du parent <span className="text-xs font-normal text-[var(--ink-soft)]">— l'enfant le découvrira en ouvrant le coffre</span></label>
    <textarea id={testid} rows={3} value={state.coffre?.message || ""} onChange={(e) => update((s) => ({ ...s, coffre: { ...(s.coffre || { opened: [] }), message: e.target.value } }))} placeholder={COFFRE_DEFAULT_MESSAGE} data-testid={testid}
      className="w-full rounded-2xl border border-[var(--border)] bg-white px-4 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[#F2B84B]" />
    <p className="text-xs text-[var(--ink-soft)] mt-1">Sans message, un encouragement par défaut est affiché. Pensez à le renouveler pour le prochain coffre !</p>
  </div>
);

export default function ScCoffre({ state, update }) {
  const info = coffreInfo(state);
  const [opening, setOpening] = useState(false);
  const [showParent, setShowParent] = useState(false);
  const child = state.setup.childName || "l'enfant";
  const lastOpened = [...(state.coffre?.opened || [])].reverse()[0];
  const shown = info.opened ? (state.coffre?.opened || []).find((o) => o.key === info.key) : null;

  const open = () => {
    setOpening(true); sfx.chest();
    const message = (state.coffre?.message || "").trim() || COFFRE_DEFAULT_MESSAGE;
    setTimeout(() => {
      update((s) => ({ ...s, coffre: { ...(s.coffre || {}), message: s.coffre?.message || "", opened: [...(s.coffre?.opened || []), { key: info.key, date: dateKey(), milestone: info.milestone, message }] } }));
      setOpening(false);
      toast(`🎁 Coffre surprise ouvert !`, { description: `${info.milestone} soirs de série — bravo ${child} !`, duration: 6000 });
    }, 900);
  };

  return (
    <div className={`rounded-3xl p-5 border-2 transition-colors ${info.available ? "bg-[#FFF3D6] border-[#F2B84B]" : info.opened ? "bg-[#E4F5EC] border-[#7CC6A6]" : "bg-white/80 border-[var(--border)]"}`} data-testid="sc-coffre" data-state={info.available ? "available" : info.opened ? "opened" : "locked"}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`text-6xl select-none ${opening ? "animate-bounce" : info.available ? "animate-pulse" : ""}`} data-testid="sc-coffre-icon">{info.opened || opening ? "🎁" : "🧰"}</div>
        <div className="flex-1">
          <div className="font-serif-title text-2xl text-[var(--ink)]">Le coffre surprise</div>
          {info.available && (
            <>
              <p className="text-sm text-[var(--ink)] mt-1" data-testid="sc-coffre-text">Série de <strong>{info.milestone} soirs</strong> atteinte : un coffre t'attend, {child} !</p>
              <button onClick={open} disabled={opening} data-testid="sc-coffre-open" className="mt-3 px-5 py-2.5 rounded-full bg-[#F2B84B] text-white text-sm font-bold shadow-sm hover:brightness-105 active:scale-95 transition-all disabled:opacity-70">{opening ? "Le coffre s'ouvre…" : "Ouvrir le coffre !"}</button>
            </>
          )}
          {info.opened && shown && (
            <div className="mt-2 rounded-2xl bg-white border border-[#7CC6A6] p-4 animate-fade-up" data-testid="sc-coffre-reveal">
              <div className="text-xs uppercase tracking-wider font-bold text-[#2F7A55] mb-1">Message de {state.setup.parentName || "ton coéquipier"} · {shown.milestone} soirs</div>
              <p className="text-base text-[var(--ink)] italic" data-testid="sc-coffre-reveal-message">« {shown.message} »</p>
              <p className="text-xs text-[var(--ink-soft)] mt-2">Prochain coffre dans {info.remaining} soir{info.remaining > 1 ? "s" : ""} de série.</p>
            </div>
          )}
          {!info.available && !info.opened && (
            <p className="text-sm text-[var(--ink-soft)] mt-1" data-testid="sc-coffre-text">
              {info.streak === 0 ? `Enchaîne ${COFFRE_EVERY} missions du soir pour ouvrir un coffre avec un message secret de ${state.setup.parentName || "ton coéquipier"}.` : `Encore ${info.remaining} soir${info.remaining > 1 ? "s" : ""} de série et le coffre s'ouvre !`}
              {lastOpened && <span className="block mt-1 text-xs">Dernier coffre ouvert le {lastOpened.date} : « {lastOpened.message} »</span>}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 border-t border-[var(--border)]/60 pt-3">
        <button onClick={() => setShowParent((v) => !v)} data-testid="sc-coffre-parent-toggle" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--gold)] hover:underline"><Pencil size={13} /> Espace du parent : écrire le message secret <ChevronDown size={13} className={`transition-transform ${showParent ? "rotate-180" : ""}`} /></button>
        {showParent && <div className="mt-3"><CoffreMessageField state={state} update={update} /></div>}
      </div>
    </div>
  );
}
